import React, {FC} from "react";
import {useAxios} from "../../../utils/hooks";
import useSafeState from "../../../utils/safe-state";
import {Trans} from "@lingui/macro";
import {Breadcrumb, Button, Divider, Popconfirm, Tooltip, Upload, UploadProps} from "antd";
import {
    DeleteTwoTone,
    PlusOutlined,
    UploadOutlined
} from "@ant-design/icons/lib";
import DtoTable from "../../../components/dto-table/dto-table";
import ButtonWithIcon from "../../../components/buttons/button-with-icon";
import LegacyStockModel from "../../../models/legacy-stock/legacy-stock-model";
import Drawer from "../../../components/drawer/drawer";
import LegacyStockDrawer from "../../../forms/legacy-stock-form";
import {DownloadOutlined, EditOutlined} from "@ant-design/icons";
import {openNotification} from "../../../utils/openNotification";

const LegacyStockScreen: FC = () => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [trigger, setTrigger] = useSafeState(false);
    const legacyStockText = <Trans>Legacy Stock</Trans>
    const [legacyStockFileList, setLegacyStockFileList] = useSafeState<Array<any>>([]);
    const [legacyStockLoading, setLegacyStockLoading] = useSafeState(false);


    const legacyStockDrawer = Drawer(
        {
            title: legacyStockText,
            children:
                <LegacyStockDrawer onClose={() => {
                    setTrigger(!trigger);
                }}/>,
            customClose: () => setTrigger(!trigger)
        }
    );
    const handleDelete = (record: any) => {

        if (axiosInstance.current != null) {
            axiosInstance.current
                .delete('/resource/legacy-stock/' + encodeURIComponent(record.id))
                .then(() => {
                    openNotification('customSuccess', 'Successfully deleted.')
                    setTrigger(false);
                    setTrigger(!trigger);
                })
                .catch(function (error: any) {
                    openNotification('error', error);
                    setTrigger(false);
                    setTrigger(!trigger);
                });
        }

    };

    const action = (record: any) => {

        return (
            <>
                <Tooltip title={<Trans>Edit</Trans>}>
                    <EditOutlined className={"ActionButtons"} style={{color: "#40a9ff"}}
                                  onClick={() =>
                                      legacyStockDrawer
                                          .open(
                                              "Edit",
                                              {
                                                  id: record.id,
                                                  item: record.item,
                                                  validTo: record.validTo,
                                                  version: record.version
                                              })
                                  }
                    />
                </Tooltip>
                <Tooltip title={<Trans>Remove</Trans>}>
                    <Popconfirm
                        title={<Trans>Are you sure want to delete record?</Trans>}
                        onConfirm={() => handleDelete(record)}
                        okText={<Trans>Yes</Trans>}
                        cancelText={<Trans>No</Trans>}>
                        <DeleteTwoTone className={"ActionButtons"} twoToneColor={"red"}/>
                    </Popconfirm>

                </Tooltip>

            </>
        )
    };

    const actions = {
        width: 100,
        render: (text: string, record: any) => action(record),
        title: '',
        fixed: 'right',
        align: 'center'
    };

    const handleDownloadXls = () => {
        if(axiosInstance.current != null){
            const FileDownload = require('js-file-download');
            axiosInstance.current({
                url: process.env.REACT_APP_API_BASE_URL + '/resource/legacy-stock/download-template',
                method: 'GET',
                responseType: 'blob'
            }).then((response) => {
                FileDownload(response.data, 'legacyStockUploadTemplate.xlsx');
            }).catch(function (error: any) {
                console.log(error);
            });
        }
    }

    const legacyStockProps: UploadProps = {
        name: 'file',
        multiple: false,
        showUploadList: false,
        customRequest: async (options) => {
            const { file, onSuccess, onError } = options;
            if (!axiosInstance.current) return;

            setLegacyStockLoading(true);
            const formData = new FormData();
            formData.append('file', file as any);

            try {
                await axiosInstance.current.post('/resource/legacy-stock/upload-xls', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                openNotification('customSuccess', 'File successfully uploaded');
                onSuccess && onSuccess({}, file as any);
            } catch (error) {
                openNotification('error', error);
                onError && onError(error as any);
            } finally {
                setLegacyStockLoading(false);
            }
        },
        onChange(info) {
            if (info.file.status === 'done') {
                setTrigger(false);
                setTrigger(true);
            }
        }
    };

    const header = (
        <>
            <Button type={'primary'} onClick={() => legacyStockDrawer.open('Add new')}>
                <ButtonWithIcon icon={<PlusOutlined />} text={<Trans>Add new</Trans>} />
            </Button>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
            <Button onClick={() => handleDownloadXls()}>
                <ButtonWithIcon
                    icon={<DownloadOutlined />}
                    text={<Trans>.xlsx template</Trans>}
                />
            </Button>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
            <Upload {...legacyStockProps}>
                <Button loading={legacyStockLoading}>
                    <ButtonWithIcon icon={<UploadOutlined/>} text={<Trans>.xlsx upload</Trans>} />
                </Button>
            </Upload>
            <Divider type="vertical" style={{ fontSize: 'xxx-large' }} />
        </>
    );

    return (<>

            <>

                <Breadcrumb style={{marginBottom: 10}}>
                    <Breadcrumb.Item><Trans id={'Home'}/></Breadcrumb.Item>
                    <Breadcrumb.Item><Trans id={'Master Data'}/></Breadcrumb.Item>
                    <Breadcrumb.Item><Trans id={'Legacy Stock'}></Trans></Breadcrumb.Item>
                </Breadcrumb>
                <DtoTable
                    tableHeader={header}
                    model={LegacyStockModel}
                    action={actions}
                    trigger={trigger}
                    allowRefreshButton={true}
                    pageSize={100}
                    scroll={{x: 1300, y: "65vh"}}/>
            </>
            {legacyStockDrawer.component}
        </>
    )
}
export default LegacyStockScreen;

